import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { API, axios } from '../utils/api';
import { useAuth, paginaDelRuolo } from '../context/AuthContext';
import { ScenaAccesso, useScenaAccesso } from '../components/accesso/ScenaAccesso';
import {
  messaggioErrore, useInvioUnico, TestaCard, Avviso, Bottone, Campo, CampoPassword, IndicatoreRobustezza, CasellaPrivacy,
} from '../components/accesso/campi';
import {
  LUNGHEZZE, campoDellErrore, erroriDatiCliente, erroriPassword, normalizzaCodice, passoDellErrore,
} from '../utils/registrazione';
import { useTraduzioniOspite } from '../utils/linguaOspite';

// Registrazione del cliente, nello stile del login: passo 1 "I vostri dati", passo 2 "La password".
// Stessa rotta di prima (POST /api/clienti/registrazione, con il codice del consulente facoltativo),
// poi login con le stesse credenziali, schermo "Benvenuti" e dashboard.
// Il passo sta nell'indirizzo (/register?passo=2): il tasto Indietro del browser torna al passo 1 invece di uscire.
// I dati del passo 1 restano in una bozza nella scheda (sessionStorage, mai la password) fino alla registrazione.

// Messaggi del backend riscritti come il resto della card (chiave del dizionario, non testo: la card è tradotta)
const TESTI_BACKEND = [
  [/già registrato|email già/i, 'accesso.email_gia_registrata', 'email'],
  [/codice/i, 'accesso.codice_non_valido', 'codice'],
];

const BOZZA = 'mk_bozza_registrazione';
const VUOTI = { nome: '', cognome: '', email: '', telefono: '', codice: '' };

const leggiBozza = () => {
  try {
    const b = JSON.parse(sessionStorage.getItem(BOZZA) || 'null');
    if (!b || typeof b !== 'object') return VUOTI;
    return Object.fromEntries(Object.keys(VUOTI).map((k) => [k, typeof b[k] === 'string' ? b[k] : '']));
  } catch { return VUOTI; }
};
const salvaBozza = (dati) => { try { sessionStorage.setItem(BOZZA, JSON.stringify(dati)); } catch { /* archivio non disponibile */ } };
const cancellaBozza = () => { try { sessionStorage.removeItem(BOZZA); } catch { /* archivio non disponibile */ } };

const Register = () => {
  const { login, isAuthenticated, userType, loading } = useAuth();
  // Fuori dall'area del cliente: la lingua è quella del browser (o quella del profilo, se si è già entrati)
  const { t, l } = useTraduzioniOspite();
  const scena = useScenaAccesso('MK Client — Registrazione');
  const { scuoti, entra } = scena;
  const location = useLocation();
  const navigate = useNavigate();
  const entrando = useRef(false);

  const [dati, setDati] = useState(leggiBozza);
  const [pw, setPw] = useState({ password: '', conferma: '', privacy: false });
  const [vedi, setVedi] = useState(false);
  const [avviso, setAvviso] = useState(null);
  const [campoErrato, setCampoErrato] = useState(null);
  const [attesa, setAttesa] = useState(false);
  // null = da verificare, false = no, { trovato, inserito_da, nome?, cognome? } = profilo già creato dal consulente
  const [profiloTrovato, setProfiloTrovato] = useState(null);
  // la verifica del codice non è riuscita (rete, server): si riprova con "Continua", intanto non si aspetta più
  const [verificaFallita, setVerificaFallita] = useState(false);
  const campi = useRef({});
  const invioContinua = useInvioUnico();
  const invioCrea = useInvioUnico();

  // Passo dall'indirizzo; il passo 2 solo con i dati del passo 1 a posto (indirizzo aperto a mano, bozza vecchia)
  const passoChiesto = new URLSearchParams(location.search).get('passo') === '2' ? 2 : 1;
  const verificaInSospeso = dati.codice.length === 8 && profiloTrovato === null && !verificaFallita;
  const datiPronti = !erroriDatiCliente(dati, { profiloGiaCreato: !!profiloTrovato }, l);
  const passo = passoChiesto === 2 && datiPronti ? 2 : 1;
  // Ricaricando al passo 2 con un codice nella bozza, finché la verifica non risponde non si sa se servono nome e
  // cognome: niente passo 1 per un attimo (prima lampeggiava), la card aspetta
  const attesaCodice = passoChiesto === 2 && passo === 1 && verificaInSospeso;
  const passoPrecedente = useRef(passo);

  useEffect(() => { salvaBozza(dati); }, [dati]);

  useEffect(() => {
    if (passoChiesto === 2 && !datiPronti && !verificaInSospeso) navigate({ search: '' }, { replace: true });
  }, [passoChiesto, datiPronti, verificaInSospeso, navigate]);

  const rif = (nome) => (el) => { campi.current[nome] = el; };

  // Al cambio di passo (non all'apertura: su telefono aprirebbe la tastiera) il fuoco va al primo
  // campo del passo, o a quello sbagliato
  useEffect(() => {
    if (passoPrecedente.current === passo) return;
    passoPrecedente.current = passo;
    campi.current[campoErrato || (passo === 1 ? (profiloTrovato ? 'email' : 'nome') : 'password')]?.focus();
  }, [passo]);

  // Codice del consulente: con 8 caratteri si controlla subito (rotta già esistente)
  const verificaCodice = async (codice) => {
    try {
      const r = await axios.get(`${API}/auth/verifica-preregistrazione`, { params: { codice } });
      return r.data.trovato ? r.data : false;
    } catch {
      return null;
    }
  };

  // Il codice si controlla appena ha 8 caratteri (quello della bozza, all'apertura, subito): "Codice non trovato" si
  // legge sotto il campo senza aspettare "Continua"
  const primaVerifica = useRef(true);
  useEffect(() => {
    const subito = primaVerifica.current;
    primaVerifica.current = false;
    setProfiloTrovato(null);
    setVerificaFallita(false);
    if (dati.codice.length !== 8) return undefined;
    let attivo = true;
    const t = setTimeout(() => verificaCodice(dati.codice).then((esito) => {
      if (!attivo) return;
      if (esito === null) setVerificaFallita(true);
      else setProfiloTrovato(esito);
    }), subito ? 0 : 300);
    return () => { attivo = false; clearTimeout(t); };
  }, [dati.codice]);

  const cambia = (campo) => (valore) => {
    setDati((d) => ({ ...d, [campo]: campo === 'codice' ? normalizzaCodice(valore) : valore }));
    if (campoErrato === campo) setCampoErrato(null);
  };

  const cambiaPw = (campo) => (valore) => {
    setPw((p) => ({ ...p, [campo]: valore }));
    if (campoErrato === campo) setCampoErrato(null);
  };

  const errore = ({ campo, testo }) => {
    setAvviso({ testo });
    setCampoErrato(campo);
    scuoti();
    campi.current[campo]?.focus();
  };

  // Torna al passo 1: con il tasto Indietro della storia se ci si è arrivati da lì, altrimenti sostituendo l'indirizzo
  const tornaAlPasso1 = () => {
    if (location.state?.dalPasso1) navigate(-1);
    else navigate({ search: '' }, { replace: true });
  };

  const continua = async (e) => {
    e.preventDefault();
    if (!invioContinua.blocca()) return;
    try {
      // con un codice valido il profilo c'è già: nome e cognome sono quelli che ha scritto il consulente
      let profilo = null;
      if (dati.codice.length === 8) {
        profilo = profiloTrovato ?? await verificaCodice(dati.codice);
        if (profilo) setProfiloTrovato(profilo);
      }
      const sbagliato = erroriDatiCliente(dati, { profiloGiaCreato: !!profilo }, l);
      if (sbagliato) { errore(sbagliato); return; }
      if (profilo === false) { errore({ campo: 'codice', testo: t(TESTI_BACKEND[1][1]) }); return; }
      setAvviso(null);
      setCampoErrato(null);
      navigate({ search: '?passo=2' }, { state: { dalPasso1: true } });
    } finally {
      invioContinua.sblocca();
    }
  };

  const indietro = () => {
    setAvviso(null);
    setCampoErrato(null);
    tornaAlPasso1();
  };

  const crea = async (e) => {
    e.preventDefault();
    const sbagliato = erroriPassword(pw, l);
    if (sbagliato) { errore(sbagliato); return; }
    // doppio Invio con la rete lenta: una richiesta sola (la seconda rispondeva "Esiste già un account")
    if (!invioCrea.blocca()) return;

    setAvviso(null);
    setAttesa(true);
    const profilo = profiloTrovato || null;
    try {
      await axios.post(`${API}/clienti/registrazione`, {
        nome: (profilo?.nome || dati.nome).trim(),
        cognome: (profilo?.cognome || dati.cognome).trim(),
        email: dati.email.trim(),
        telefono: dati.telefono.trim(),
        password: pw.password,
        codice_collegamento: dati.codice || null,
      });
    } catch (err) {
      invioCrea.sblocca();
      setAttesa(false);
      const dettaglio = typeof err.response?.data?.detail === 'string' ? err.response.data.detail : '';
      const noto = TESTI_BACKEND.find(([re]) => re.test(dettaglio));
      // 422: l'email non passa il controllo del server
      if (err.response?.status === 422) { tornaAlPasso1(); errore({ campo: 'email', testo: t('accesso.email_incompleta') }); return; }
      if (noto || passoDellErrore(dettaglio) === 1) {
        tornaAlPasso1();
        errore({
          campo: noto?.[2] || campoDellErrore(dettaglio) || 'email',
          testo: noto ? t(noto[1]) : messaggioErrore(err, t('accesso.registrazione_non_riuscita'), l),
        });
        return;
      }
      errore({ campo: campoDellErrore(dettaglio), testo: messaggioErrore(err, t('accesso.registrazione_non_riuscita_riprovate'), l) });
      return;
    }

    // Account creato (si resta bloccati: un secondo invio direbbe "Esiste già"): si entra con le stesse credenziali
    cancellaBozza();
    entrando.current = true;
    const esito = await login(dati.email.trim(), pw.password, true);
    if (!esito.success) {
      entrando.current = false;
      setAttesa(false);
      setAvviso({ testo: t('accesso.account_creato_accedete'), ok: true });
      return;
    }
    entra(esito, { parola: 'accesso.benvenuti', etichetta: 'accesso.account_creato' });
  };

  // Già entrati: alla propria area, non al modulo
  if (!loading && isAuthenticated && !entrando.current) return <Navigate to={paginaDelRuolo(userType)} replace />;

  const nomeDalProfilo = !!profiloTrovato?.nome;
  const codiceNonTrovato = profiloTrovato === false && dati.codice.length === 8;
  const passoVisto = attesaCodice ? 2 : passo;

  return (
    <ScenaAccesso scena={scena}>
      <TestaCard etichetta={t(`accesso.saluto.${scena.ora}`)} titolo={t('accesso.reg_titolo')} corsivo={t('accesso.reg_titolo_corsivo')}
        sotto={t('accesso.reg_sotto')} />

      <div className="body">
        <div className="passi">
          <div className="passi-riga">
            {passo === 2 && (
              <a href="#indietro" role="button" className="torna" data-testid="registrazione-indietro"
                onClick={(e) => { e.preventDefault(); indietro(); }}>← {t('comune.indietro')}</a>
            )}
            <span className="passo">
              {t('accesso.passo', { n: passoVisto })} · <b>{t(passoVisto === 1 ? 'accesso.passo_1' : 'accesso.passo_2')}</b>
            </span>
          </div>
          <div className="avanzamento" role="progressbar" aria-label={t('accesso.avanzamento')}
            aria-valuemin={1} aria-valuemax={2} aria-valuenow={passoVisto}>
            <i style={{ width: passoVisto === 1 ? '50%' : '100%' }} />
          </div>
        </div>

        <Avviso avviso={avviso} />

        {attesaCodice ? (
          <p className="sent intro attesa-codice" role="status" data-testid="registrazione-attesa-codice">{t('accesso.attesa_codice')}</p>
        ) : passo === 1 ? (
          <form onSubmit={continua} noValidate data-testid="registrazione-passo-1">
            {/* profilo già creato dal consulente: nome e cognome sono i suoi (mostrati se il server li dà) */}
            {(!profiloTrovato || nomeDalProfilo) && (
              <div className="coppia">
                <Campo id="reg-nome" etichetta={t('profilo.nome')} autoComplete="given-name" autoCapitalize="words" ref={rif('nome')}
                  maxLength={LUNGHEZZE.nome} readOnly={nomeDalProfilo} valore={nomeDalProfilo ? profiloTrovato.nome : dati.nome}
                  onChange={cambia('nome')} invalido={campoErrato === 'nome'} data-testid="register-nome" />
                <Campo id="reg-cognome" etichetta={t('profilo.cognome')} autoComplete="family-name" autoCapitalize="words" ref={rif('cognome')}
                  maxLength={LUNGHEZZE.cognome} readOnly={nomeDalProfilo} valore={nomeDalProfilo ? (profiloTrovato.cognome || '') : dati.cognome}
                  onChange={cambia('cognome')} invalido={campoErrato === 'cognome'} data-testid="register-cognome" />
              </div>
            )}
            <Campo id="reg-email" etichetta={t('accesso.email')} type="email" inputMode="email" autoComplete="email" ref={rif('email')}
              maxLength={LUNGHEZZE.email} valore={dati.email} onChange={cambia('email')} invalido={campoErrato === 'email'} data-testid="register-email" />
            <Campo id="reg-telefono" etichetta={t('accesso.telefono_whatsapp')} type="tel" inputMode="tel" autoComplete="tel" ref={rif('telefono')}
              maxLength={LUNGHEZZE.telefono} valore={dati.telefono} onChange={cambia('telefono')} invalido={campoErrato === 'telefono'} data-testid="register-telefono" />
            <Campo id="reg-codice" classe="lungo" etichetta={t('accesso.codice_consulente')} autoComplete="off" autoCapitalize="characters"
              spellCheck={false} ref={rif('codice')}
              valore={dati.codice} onChange={cambia('codice')} invalido={campoErrato === 'codice' || codiceNonTrovato} data-testid="register-codice" />
            <p className={profiloTrovato ? 'aiuto ok' : codiceNonTrovato ? 'aiuto errore' : 'aiuto'} aria-live="polite" data-testid="register-codice-aiuto">
              {profiloTrovato
                ? t('accesso.profilo_trovato', {
                  chi: profiloTrovato.inserito_da,
                  nome: nomeDalProfilo ? t('accesso.profilo_trovato_nome', { nome: `${profiloTrovato.nome} ${profiloTrovato.cognome || ''}`.trimEnd() }) : '',
                })
                : t(codiceNonTrovato ? 'accesso.codice_non_trovato' : 'accesso.codice_facoltativo')}
            </p>
            <Bottone data-testid="registrazione-continua">{t('accesso.continua')}</Bottone>
          </form>
        ) : (
          <form onSubmit={crea} noValidate data-testid="registrazione-passo-2">
            <CampoPassword id="reg-password" autoComplete="new-password" ref={rif('password')}
              valore={pw.password} onChange={cambiaPw('password')} invalido={campoErrato === 'password'}
              visibile={vedi} onVisibile={() => setVedi((v) => !v)} data-testid="register-password" />
            <IndicatoreRobustezza password={pw.password} />
            <CampoPassword id="reg-conferma" etichetta={t('accesso.ripetete_password')} autoComplete="new-password" ref={rif('conferma')}
              valore={pw.conferma} onChange={cambiaPw('conferma')} invalido={campoErrato === 'conferma'}
              visibile={vedi} data-testid="register-conferma" />
            <CasellaPrivacy spuntata={pw.privacy} onChange={cambiaPw('privacy')} invalida={campoErrato === 'privacy'} ref={rif('privacy')} />
            <Bottone attesa={attesa} data-testid="registrazione-crea">{t('accesso.crea_account')}</Bottone>
          </form>
        )}

        <div className="links">
          {t('accesso.gia_account')} <Link to="/login">{t('accesso.accedete')}</Link>
        </div>
      </div>
    </ScenaAccesso>
  );
};

export default Register;
